import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useFranchiseData } from '../hooks/useFranchiseData'
import SEO from '../components/SEO'
import { buildWebPageSchema } from '../utils/schema'
import { RouteLayout, RouteSection } from '../components/RouteLayout'
import { getWhatsAppUrl } from '../utils/whatsapp'

const faqs = [
  {
    question: 'Do I need a visa for my holiday?',
    answer: 'It depends on your destination, nationality and length of stay. Our team confirms this for you as part of every booking.',
  },
  {
    question: 'How long does a visa application take?',
    answer: 'Timelines vary — from a few days for e-visas to several weeks for embassy applications. We give you a realistic timeline as soon as you book.',
  },
  {
    question: 'What documents do I typically need?',
    answer: 'Most applications require a valid passport, passport photos, proof of accommodation, return flights, and sometimes travel insurance or bank statements.',
  },
  {
    question: 'What if my visa is delayed or rejected?',
    answer: 'We help you understand backup options and next steps — a trip goes far beyond just the booking.',
  },
]

const steps = [
  {
    title: 'We check what you need',
    body: 'Requirements confirmed for your nationality, destination and dates.',
  },
  {
    title: 'We guide documentation',
    body: 'A clear checklist so nothing gets missed.',
  },
  {
    title: 'We support your application',
    body: 'Step-by-step help with e-visas, visa-on-arrival or embassy applications.',
  },
  {
    title: "We're on call before you fly",
    body: 'A WhatsApp message away, right up until departure.',
  },
]

export default function VisaSupport() {
  const { franchiseData } = useFranchiseData()
  const [openIndex, setOpenIndex] = useState(0)
  const brandName = franchiseData?.name || 'VAIA World'
  const whatsappUrl = getWhatsAppUrl(
    franchiseData,
    'Hi, I would like visa guidance from a VAIA World Lifestyle Planner.'
  )

  return (
    <div className="bg-cream">
      <SEO
        title={`Visa Support | ${brandName}`}
        description="Visa support, simplified. Every VAIA World Lifestyle Planner guides you through requirements, documents, and applications."
        canonical={typeof window !== 'undefined' ? window.location.href : undefined}
        openGraph={{
          type: 'website',
          title: `Visa Support | ${brandName}`,
          url: typeof window !== 'undefined' ? window.location.href : undefined,
        }}
        twitter={{ card: 'summary_large_image' }}
        jsonLd={buildWebPageSchema({
          name: 'Visa Support',
          description: 'Visa support, simplified. Every VAIA World Lifestyle Planner guides you through requirements, documents, and applications.',
          url: typeof window !== 'undefined' ? window.location.href : ''
        })}
      />

      <RouteLayout>
        <RouteSection mark={1} className="hero !pt-[70px]" markTop={70}>
          <div className="eyebrow">Visa Support</div>
          <h1 className="text-[clamp(32px,5vw,50px)] leading-[1.02] m-0">
            Visa support,<br />simplified.
          </h1>
          <p className="text-lg text-ink-soft max-w-[560px] mt-5 leading-relaxed">
            Planning a trip is exciting — visa paperwork shouldn't slow you down. Every VAIA World Lifestyle Planner guides you through requirements, documents, and applications.
          </p>
          <div className="flex gap-3.5 mt-8 flex-wrap">
            <a
              href={whatsappUrl}
              target={whatsappUrl.startsWith('http') ? '_blank' : undefined}
              rel={whatsappUrl.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="btn-primary"
            >
              Chat to a Lifestyle Planner
            </a>
            <Link to="/packages" className="btn-ghost">
              See Visa-Free Destinations
            </Link>
          </div>
        </RouteSection>

        <RouteSection mark={2} className="!pt-0">
          <div className="sec-head">
            <h2>How it works</h2>
          </div>
          <div className="cards cards-4">
            {steps.map((step, index) => (
              <div key={step.title} className="card-vaia">
                <div className="card-body">
                  <div className="font-mono text-vaia-amber-deep text-xs">STEP {index + 1}</div>
                  <h3 className="mt-2 text-[15px]">{step.title}</h3>
                  <p className="text-[13px] text-ink-soft mt-2 mb-0">{step.body}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <a
              href="https://vaiavisa.co.za"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Get Started
            </a>
          </div>
        </RouteSection>

        <RouteSection mark={3} className="!pt-0 !max-w-[820px]">
          <div className="sec-head">
            <h2>Frequently asked questions</h2>
          </div>
          <div>
            {faqs.map((faq, index) => (
              <div key={faq.question} className={`faq-item ${openIndex === index ? 'open' : ''}`}>
                <button
                  type="button"
                  className="faq-q"
                  onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                >
                  {faq.question}
                  <span className="faq-plus">+</span>
                </button>
                <div className="faq-a">{faq.answer}</div>
              </div>
            ))}
          </div>
        </RouteSection>
      </RouteLayout>
    </div>
  )
}
